import { useCallback, useState } from 'react';
import { Box, TextField, Button, Typography, Modal, Divider, Popover, Grow } from '@mui/material';
import { Sheet } from '@mui/joy';
import { Close } from '@mui/icons-material';
import toast from 'react-hot-toast';
import { FirstLetterCaps, getHeaders, rootColorFilter } from '../../../../Helpers/NCRHelpers';
import axios from 'axios';
import { NCRTooltip } from '../../../Utilities/NCRFormHeading';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery'

export default function FishBonePage({ fishboneState }) {
    const { formData, fishBoneForm, setFishBoneForm, prevFishBoneForm, setPrevFishBoneForm, isEditBtn, setIsEditBtn } = fishboneState;
    const theme = useTheme();
    const isCustom = useMediaQuery('(min-width:1500px) and (max-width:1800px)')
    const isLaptop = useMediaQuery(theme.breakpoints.only('lg')) || isCustom
    const isCustom1 = useMediaQuery('(min-width:1200px) and (max-width:1350px)')
    const isTab = useMediaQuery(theme.breakpoints.down('lg')) || isCustom1

    const [isOpen, setIsOpen] = useState(false);
    const [messagePopup, setMessagePopup] = useState(false);
    const [categoryValue, setCategoryValue] = useState('');
    const [fieldText, setFieldText] = useState({
        pageValue: "", color: "",
        value: "",
        id: 0
    });

    const handleClick = (event) => setMessagePopup(event.currentTarget);

    const handleFieldClick = (fieldName = '', color, id, dataValue) => {
        setIsOpen(true);
        setCategoryValue(dataValue || '');
        setFieldText({
            ...fieldText,
            pageValue: dataValue ? 'Edit' : 'Create',
            id,
            color,
            value: fieldName || '',
        });
    };

    const handleHighlighted = (fieldName = '', id, highlightVal) => {
        const lowerCaseField = fieldName?.toLowerCase();
        const updatedFieldItems = fishBoneForm[lowerCaseField]?.map((item, idx) => {
            return {
                ...item,
                is_highlighted: idx === id ? !highlightVal : false
            }
        });

        setFishBoneForm(prev => ({
            ...prev,
            [lowerCaseField]: updatedFieldItems
        }));
        toast.success(`${FirstLetterCaps(fieldName?.toLocaleLowerCase())} sub cause highlight ${highlightVal ? 'removed' : 'applied'} successfully`);
    };

    const handleSave = async () => {
        if (!categoryValue?.trim()) {
            toast.error(`Please fill the sub cause field`);
            return;
        }

        const isEdit = fieldText?.pageValue === "Edit";
        const formCategoryKey = fieldText?.value?.toLowerCase();

        setFishBoneForm(prev => {
            const updated = structuredClone(prev);
            const items = updated[formCategoryKey] || [];

            if (items[fieldText.id]) {
                items[fieldText.id] = {
                    ...items[fieldText.id],
                    data: categoryValue?.trim() || '',
                };
            }

            updated[formCategoryKey] = items;
            return updated;
        });

        toast.success(`${FirstLetterCaps(fieldText.value?.toLocaleLowerCase())} sub cause ${isEdit ? 'edited' : 'added'} successfully`);
        setCategoryValue('');
        setIsOpen(false);
    };

    const submitFishboneData = async (ncrId = '', payload = {}) => {
        try {
            const response = await axios.put(`${process.env.REACT_APP_BACKEND_BASE_URL}/fishbone/${ncrId}`, payload, getHeaders());
            toast.success("Fishbone added to 8D Report Successfully");
            setIsEditBtn(true);
            setPrevFishBoneForm(fishBoneForm);
            return response?.data?.data || [];
        } catch (error) {
            toast.error(error?.message || 'Submission failed');
            return [];
        }
    };

    const handleApiCall = async () => await submitFishboneData(formData?.ncr_id, fishBoneForm);

    const resetForm = useCallback(() => {
        setFishBoneForm(prevFishBoneForm)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prevFishBoneForm]);

    const renderFishBoneTextField = ({
        field,
        index,
        color = '#FCB433',
        onClick
    }) => {
        const item = fishBoneForm?.[field.toLowerCase()]?.[index];
        if (!item) return null;
        const capsMatch = item?.data?.match(/[A-Z]/g);
        const hasMoreThanOneUppercase = capsMatch && capsMatch.length > 1;

        const inCaps = hasMoreThanOneUppercase ? 19 : 21;

        const displayValue = item?.data?.length > inCaps ? item.data.slice(0, inCaps) + "..." : item?.data || "";

        return <Box sx={{
            marginTop: "5px",
            cursor: !isEditBtn && "pointer",
            color: (displayValue && !item?.is_highlighted) ? '#616a6b' : (displayValue && item?.is_highlighted) ? 'white' : "rgba(128, 128, 128, 1)",
            maxHeight: isTab ? '20px' : isLaptop ? '30px' : '45px',
            padding: isTab ? '5px' : '8px 14px',
            fontSize: (isTab && displayValue) ? '5.5px' : (isTab && !displayValue) ? '5px' : (isLaptop && displayValue) ? '8px' : (isLaptop && !displayValue) ? '9px' : displayValue ? '13px' : '14px',
            minWidth: '100%',
            width: isTab ? '72px' : isLaptop ? '120px' : "175px",
            border: (!displayValue && !item?.is_highlighted) && "1px solid rgba(128, 128, 128, 0.4)",
            borderRadius: isTab ? '3px' : isLaptop ? '4px' : "5px",
            display: "flex",
            alignItems: 'center',
            justifyContent: (displayValue && !item?.is_highlighted) ? "right" : displayValue ? "center" : 'center',
            backgroundColor: item?.is_highlighted ? rootColorFilter(field) : 'transparent',
            fontWeight: (displayValue) && 600,
        }}
            onClick={() => !isEditBtn && onClick(field.toUpperCase(), color, index, item.data)}
        >
            <NCRTooltip title={((item?.data?.length > inCaps) && displayValue) && item?.data}>
                {displayValue ? FirstLetterCaps(displayValue) : 'Click here to enter'}
            </NCRTooltip>
        </Box>
    };

    const HighlightArrow = ({ fieldName, id, filedData, style }) => {
        if (!filedData) return;
        const { is_highlighted, data } = filedData
        return (
            <div
                onClick={() => {
                    if (isEditBtn) return
                    if (!data) toast.error("Sub-cause is required before selection."); else handleHighlighted(fieldName, id, is_highlighted)
                }}
                style={{
                    position: "absolute",
                    cursor: !isEditBtn && "pointer",
                    backgroundColor: rootColorFilter(fieldName),
                    width: isTab ? '19px' : isLaptop ? '30px' : '38px',
                    height: isTab ? '16px' : isLaptop ? '25px' : "33px",
                    clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
                    ...style
                }}
            />
        )
    };

    console.log("isLaptop", { isLaptop, isTab });
    return (
        <>
            <Box sx={{ width: "100%", zIndex: 1 }}>
                <Grow
                    in={true}
                    style={{ transformOrigin: '0 0 0' }}
                    timeout={1000}
                >
                    <Box sx={{ p: 4, py: 8, width: "100%" }}>
                        <Typography sx={{ fontFamily: 'Lato, sans-serif',fontSize: "16px", margin: '0 auto' }}>
                            <span style={{ fontWeight: 600 }}> Note :</span> Identify the most likely or frequent contributors to the problem, perform a 'Why-Why' analysis on these significant causes,
                            and click the hexagon button to highlight them for further focus.
                        </Typography>
                        <Box sx={{ overflow: "auto" }}>
                            <div style={{ position: 'relative', margin: '0 auto', zIndex: 0 }}>
                                <img
                                    src="/images/fish-bone-2.png"
                                    style={{ width: '100%', height: 'auto', display: 'block' }}
                                    alt="fish"
                                />

                                <div>
                                    <Typography style={{ fontFamily: 'Lato, sans-serif', position: 'absolute', top: '12%', left: '17.7%', transform: 'translate(-50%, -50%)', fontWeight: 600, color: '#fff', fontSize: isTab ? '8px' : isLaptop && '12.5px' }}>MANPOWER</Typography>

                                    <div style={{ position: 'absolute', top: '23%', left: '17.5%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px" }}>
                                        {renderFishBoneTextField({ field: 'MANPOWER', index: 0, color: '#FCB433', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={0}
                                        fieldName={'MANPOWER'}
                                        filedData={fishBoneForm?.manpower[0]}
                                        style={{ top: '21%', left: '24.5%' }}
                                    />

                                    <div style={{ position: 'absolute', top: '31%', left: '21.5%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px" }}>
                                        {renderFishBoneTextField({ field: 'MANPOWER', index: 1, color: '#FCB433', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={1}
                                        fieldName={'MANPOWER'}
                                        filedData={fishBoneForm?.manpower[1]}
                                        style={{ top: '28.5%', left: '28.3%', }}
                                    />

                                    <div style={{ position: 'absolute', top: '38.5%', left: '25.2%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px" }}>
                                        {renderFishBoneTextField({ field: 'MANPOWER', index: 2, color: '#FCB433', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={2}
                                        fieldName={'MANPOWER'}
                                        filedData={fishBoneForm?.manpower[2]}
                                        style={{ top: '36%', left: '32%' }}
                                    />
                                </div>

                                <div>
                                    <Typography style={{ fontFamily: 'Lato, sans-serif', position: 'absolute', top: '12%', left: '33.6%', transform: 'translate(-50%, -50%)', fontWeight: 600, color: '#fff', fontSize: isTab ? '8px' : isLaptop && '12.5px' }}>METHOD</Typography>

                                    <div style={{ position: 'absolute', top: '23%', left: '33.8%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px" }}>
                                        {renderFishBoneTextField({ field: 'METHOD', index: 0, color: '#F14B62', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={0}
                                        fieldName={'METHOD'}
                                        filedData={fishBoneForm?.method[0]}
                                        style={{ top: '20.5%', left: '40.5%' }}
                                    />

                                    <div style={{ position: 'absolute', top: '31%', left: '37.7%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px" }}>
                                        {renderFishBoneTextField({ field: 'METHOD', index: 1, color: '#F14B62', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={1}
                                        fieldName={'METHOD'}
                                        filedData={fishBoneForm?.method[1]}
                                        style={{ top: '28.5%', left: '44.2%' }}
                                    />

                                    <div style={{ position: 'absolute', top: '38%', left: '41.3%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px" }}>
                                        {renderFishBoneTextField({ field: 'METHOD', index: 2, color: '#F14B62', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={2}
                                        fieldName={'METHOD'}
                                        filedData={fishBoneForm?.method[2]}
                                        style={{ top: '36%', left: '47.9%' }}
                                    />
                                </div>

                                <div>
                                    <Typography style={{ fontFamily: 'Lato, sans-serif', position: 'absolute', top: '12%', left: '49.5%', transform: 'translate(-50%, -50%)', fontWeight: 600, color: '#fff', fontSize: isTab ? '8px' : isLaptop && '12.5px' }}>MACHINE</Typography>

                                    <div style={{ position: 'absolute', top: '23%', left: '49.8%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px" }}>
                                        {renderFishBoneTextField({ field: 'MACHINE', index: 0, color: '#BC3743', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={0}
                                        fieldName={'MACHINE'}
                                        filedData={fishBoneForm?.machine[0]}
                                        style={{ top: '21%', left: '56.5%' }}
                                    />

                                    <div style={{ position: 'absolute', top: '31%', left: '53.7%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px" }}>
                                        {renderFishBoneTextField({ field: 'MACHINE', index: 1, color: '#BC3743', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={1}
                                        fieldName={'MACHINE'}
                                        filedData={fishBoneForm?.machine[1]}
                                        style={{ top: '28.5%', left: '60.2%' }}
                                    />

                                    <div style={{ position: 'absolute', top: '38.5%', left: '57.3%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px" }}>
                                        {renderFishBoneTextField({ field: 'MACHINE', index: 2, color: '#BC3743', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={2}
                                        fieldName={'MACHINE'}
                                        filedData={fishBoneForm?.machine[2]}
                                        style={{ top: '36%', left: '63.8%' }}
                                    />
                                </div>

                                <div>
                                    <Typography style={{ fontFamily: 'Lato, sans-serif', position: 'absolute', top: '85.7%', left: '50%', transform: 'translate(-50%, -50%)', fontWeight: 600, color: '#fff', fontSize: isTab ? '8px' : isLaptop && '12.5px' }}>MEASUREMENT</Typography>
                                    <div style={{ position: 'absolute', top: '59%', left: '57.5%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px", color: 'black' }}>
                                        {renderFishBoneTextField({ field: 'MEASUREMENT', index: 0, color: '#66C068', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={0}
                                        fieldName={'MEASUREMENT'}
                                        filedData={fishBoneForm?.measurement[0]}
                                        style={{ bottom: '38.2%', left: '63.9%' }}
                                    />

                                    <div style={{ position: 'absolute', top: '66.8%', left: '53.7%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px" }}>
                                        {renderFishBoneTextField({ field: 'MEASUREMENT', index: 1, color: '#66C168', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={1}
                                        fieldName={'MEASUREMENT'}
                                        filedData={fishBoneForm?.measurement[1]}
                                        style={{ bottom: '30.8%', left: '60.2%' }}
                                    />

                                    <div style={{ position: 'absolute', top: '74%', left: '50%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px" }}>
                                        {renderFishBoneTextField({ field: 'MEASUREMENT', index: 2, color: '#66C268', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={2}
                                        fieldName={'MEASUREMENT'}
                                        filedData={fishBoneForm?.measurement[2]}
                                        style={{ bottom: '23.5%', left: '56.5%' }}
                                    />
                                </div>

                                <div>
                                    <Typography style={{ fontFamily: 'Lato, sans-serif', position: 'absolute', top: '85.7%', left: '34%', transform: 'translate(-50%, -50%)', fontWeight: 600, color: '#fff', fontSize: isTab ? '8px' : isLaptop && '12.5px' }}>ENVIRONMENT</Typography>
                                    <div style={{ position: 'absolute', top: '59%', left: '41.5%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px", color: 'black' }}>
                                        {renderFishBoneTextField({ field: 'ENVIRONMENT', index: 0, color: '#3DC1F2', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={0}
                                        fieldName={'ENVIRONMENT'}
                                        filedData={fishBoneForm?.environment[0]}
                                        style={{ bottom: '38.2%', left: '47.9%' }}
                                    />

                                    <div style={{ position: 'absolute', top: '67%', left: '37.8%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px" }}>
                                        {renderFishBoneTextField({ field: 'ENVIRONMENT', index: 1, color: '#3DC1F2', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={1}
                                        fieldName={'ENVIRONMENT'}
                                        filedData={fishBoneForm?.environment[1]}
                                        style={{ bottom: '30.7%', left: '44.3%' }}
                                    />

                                    <div style={{ position: 'absolute', top: '74%', left: '34%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px" }}>
                                        {renderFishBoneTextField({ field: 'ENVIRONMENT', index: 2, color: '#3DC1F2', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={2}
                                        fieldName={'ENVIRONMENT'}
                                        filedData={fishBoneForm?.environment[2]}
                                        style={{ bottom: '23.5%', left: '40.6%' }}
                                    />
                                </div>

                                <div>
                                    <Typography style={{ fontFamily: 'Lato, sans-serif', position: 'absolute', top: '85.7%', left: '18%', transform: 'translate(-50%, -50%)', fontWeight: 600, color: '#fff', fontSize: isTab ? '8px' : isLaptop && '12.5px' }}>MATERIAL</Typography>
                                    <div style={{ position: 'absolute', top: '59%', left: '25.5%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px", color: 'black' }}>
                                        {renderFishBoneTextField({ field: 'MATERIAL', index: 0, color: '#A66DF5', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={0}
                                        fieldName={'MATERIAL'}
                                        filedData={fishBoneForm?.material[0]}
                                        style={{ bottom: '38.4%', left: '32.1%' }}
                                    />

                                    <div style={{ position: 'absolute', top: '66.5%', left: '21.7%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px" }}>
                                        {renderFishBoneTextField({ field: 'MATERIAL', index: 1, color: '#A66DF5', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={1}
                                        fieldName={'MATERIAL'}
                                        filedData={fishBoneForm?.material[1]}
                                        style={{ bottom: '31%', left: '28.3%' }}
                                    />

                                    <div style={{ position: 'absolute', top: '74%', left: '18%', transform: 'translate(-50%, -50%)', padding: '0 2px', borderRadius: "5px" }}>
                                        {renderFishBoneTextField({ field: 'MATERIAL', index: 2, color: '#A66DF5', onClick: handleFieldClick })}
                                    </div>

                                    <HighlightArrow
                                        id={2}
                                        fieldName={'MATERIAL'}
                                        filedData={fishBoneForm?.material[2]}
                                        style={{ bottom: '23.3%', left: '24.7%' }}
                                    />
                                </div>

                                <Box style={{ position: 'absolute', top: '50%', right: isLaptop ? '4%' : '2.5%', transform: 'translate(-50%, -50%)', padding: '0 2px', width: isTab ? '90px' : isLaptop ? '130px' : "200px", height: isTab ? '35px' : isLaptop ? '60px' : "80px", borderRadius: "5px", backgroundColor: "#F14B62", display: 'flex', justifyContent: "center", alignItems: "center" }}>
                                    <div style={{ position: "relative" }}>
                                        <Button onMouseEnter={handleClick} sx={{
                                            fontFamily: 'Lato, sans-serif', cursor: "pointer", color: "#fff", fontWeight: 600, fontSize: isTab ? '10px' : isLaptop ? '15px' : "25px"
                                        }}>
                                            PROBLEM
                                        </Button>
                                    </div>
                                    <Popover
                                        id={messagePopup ? 'simple-popover' : undefined}
                                        open={messagePopup}
                                        anchorEl={messagePopup}
                                        anchorOrigin={{
                                            vertical: 'bottom',
                                            horizontal: 'left',
                                        }}
                                        PaperProps={{
                                            sx: {
                                                width: '350px',
                                                maxWidth: '90vw',
                                                ml: -10,
                                                mt: 2
                                            }
                                        }}
                                        onClose={() => setMessagePopup(false)}
                                    >
                                        <div style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            backgroundColor: "#f2f3f4",
                                            height: "45px"
                                        }}
                                            onClick={() => setMessagePopup(false)}
                                        >
                                            <Typography sx={{ fontFamily: 'Lato, sans-serif', p: 2 }}>What went wrong</Typography>
                                            <Close size='md' variant="plain" style={{
                                                color: 'rgb(138, 138, 138)',
                                                marginRight: "15px", cursor: 'pointer',
                                                border: "2px solid rgb(138, 138, 138)",
                                                borderRadius: '20px',
                                                margin: "5px 10px",
                                                fontSize: "20px",
                                            }} onClick={() => setIsOpen(false)} />
                                        </div>
                                        <Typography sx={{ fontFamily: 'Lato, sans-serif', p: 2 }}>{FirstLetterCaps(formData?.what_went_wrong + '.') || '-'}</Typography>
                                    </Popover>
                                </Box>
                            </div>
                        </Box>
                        {Object.entries(fishBoneForm)?.flatMap(([category, items]) => items?.filter(item => item.is_highlighted))?.length > 0 && <Typography sx={{ fontSize: "20px", margin: '0 auto', fontWeight: 900 }}>
                            Highlighted Sub-cause
                        </Typography>}
                        <div style={{ textWrap: "wrap", width: "100%", marginTop: "10px", display: "flex", flexDirection: "column", flexWrap: "wrap", gap: "15px" }}>
                            {Object.entries(fishBoneForm).flatMap(([category, items]) =>
                                items?.filter(item => item.is_highlighted)?.map(item => {
                                    const enrichedItem = { ...item, rootName: category };
                                    return (
                                        <div key={`${category}-${item.id}`} style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%" }}>
                                            <div style={{
                                                backgroundColor: rootColorFilter(enrichedItem?.rootName?.toUpperCase()),
                                                width: '40px',
                                                height: "33px",
                                                clipPath: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)'
                                            }}
                                            />
                                            <div style={{ width: "97.5%", textWrap: "wrap" }}>
                                                {FirstLetterCaps(enrichedItem?.data)}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </Box>
                </Grow>

                <Modal
                    aria-labelledby="modal-title"
                    aria-describedby="modal-desc"
                    open={isOpen}
                    onClose={() => setIsOpen(false)}
                    sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
                >
                    <Sheet
                        variant="outlined"
                        sx={{ width: '400px', borderRadius: 'md', boxShadow: 'lg' }}
                    >
                        <div style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center"
                        }}>
                            <Typography component="h2" sx={{
                                fontFamily: 'Lato, sans-serif',
                                fontSize: '16px',
                                fontWeight: 900,
                                color: fieldText?.color || '#000000',
                                px: 3,
                                py: 0.8,
                                mt: 1
                            }}>
                                {fieldText?.value || ''}
                            </Typography>
                            <Close size='lg' variant="plain" style={{ marginRight: "15px", cursor: 'pointer' }} onClick={() => setIsOpen(false)} />
                        </div>
                        <Divider sx={{ mb: 2, mt: 1 }} />
                        <Box sx={{ display: 'flex', mb: 2, px: 3 }}>
                            <TextField
                                className='add-user-textfield'
                                name="clientId"
                                size="small"
                                placeholder="Type Sub-cause here..."
                                fullWidth
                                multiline
                                rows={5.5}
                                value={categoryValue}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    if (value?.length > 250) return toast.error("Maximum 250 characters allowed");
                                    setCategoryValue(value)
                                }}
                                autoFocus={isOpen}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', pt: 1, pb: 2 }}>
                            <Box sx={{ display: 'flex', gap: "10px", px: 3 }}>
                                <Button
                                    onClick={() => handleSave()}
                                    variant="outlined"
                                    color="neutral"
                                    type="submit"
                                    sx={{ fontFamily: 'Lato', fontWeight: '700', fontSize: '14px', background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)', textTransform: 'none', color: 'rgba(255, 255, 255, 1)', borderRadius: '10px', py: 1, px: 3 }}
                                >
                                    Ok
                                </Button>
                            </Box>
                        </Box>
                    </Sheet>
                </Modal>
            </Box>

            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                px: 3, py: 2,
                position: 'sticky',
                bottom: 0,
                backgroundColor: 'rgba(255, 255, 255, 1)',
                zIndex: 10,
                borderRadius: '0px 0px 10px 10px',
                boxShadow: '0px -1px 4px 0px rgba(0, 0, 0, 0.15)'
            }}>
                {isEditBtn ?
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Button
                            variant="outlined"
                            color="neutral"
                            sx={{
                                background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                                textTransform: 'none',
                                fontFamily: 'Lato, sans-serif',
                                fontSize: '14px',
                                fontWeight: '700',
                                color: 'rgba(255, 255, 255, 1) !important',
                                borderRadius: '10px',
                                border: 'none !important',
                                p: 1,
                                px: 2,
                            }}
                            onClick={() => setIsEditBtn(false)}
                        >
                            Edit
                        </Button>
                    </Box>
                    :
                    <>
                        <Box sx={{ px: 2, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                color="neutral"
                                sx={{
                                    fontFamily: 'Lato, sans-serif',
                                    fontSize: '14px',
                                    fontWeight: '400',
                                    border: '1px solid rgba(14, 41, 102, 1) !important',
                                    textTransform: 'none',
                                    borderRadius: '10px',
                                    color: 'rgba(14, 41, 102, 1) !important',
                                    py: 1
                                }}
                                onClick={() => resetForm()}
                            >
                                Reset
                            </Button>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                            <Button
                                variant="outlined"
                                color="neutral"
                                sx={{
                                    background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                                    textTransform: 'none',
                                    fontFamily: 'Lato, sans-serif',
                                    fontSize: '14px',
                                    fontWeight: '700',
                                    color: 'rgba(255, 255, 255, 1) !important',
                                    borderRadius: '10px',
                                    border: 'none !important',
                                    p: 1,
                                    px: 2,
                                    opacity: !Object.values(fishBoneForm).some(items => items.some(item => item.is_highlighted)) ? 0.7 : 1
                                }}
                                disabled={!Object.values(fishBoneForm).some(items => items.some(item => item.is_highlighted))}
                                onClick={() => handleApiCall()}
                            >
                                Submit
                            </Button>
                        </Box>
                    </>
                }
            </Box>
        </>
    );
}
